/**
 * The user's profile: display name, role, avatar choice and comfort settings, persisted in
 * localStorage under the spacexr: prefix.
 */
import { RoleSchema, type Role } from '@clinspace/shared';
import { loadItem, saveItem } from '../lib/storage.ts';

export const BUILT_IN_AVATARS = [
  { id: 'avatar-01', name: 'Louise', look: 'Navy pantsuit' },
  { id: 'avatar-02', name: 'Martha', look: 'Cardigan, chinos' },
  { id: 'avatar-03', name: 'Elizabeth', look: 'Sweater, jeans' },
  { id: 'avatar-04', name: 'Joe', look: 'Suit and tie' },
  { id: 'avatar-05', name: 'Lewis', look: 'Dress shirt' },
  { id: 'avatar-06', name: 'Chad', look: 'Scrubs' },
] as const;

export type AvatarId = (typeof BUILT_IN_AVATARS)[number]['id'];

export interface Profile {
  name: string;
  role: Role;
  avatar: AvatarId;
  vrmUrl?: string;
  smoothLocomotion: boolean;
  snapAngleDeg: number;
  /** Whether the onboarding has been completed at least once. */
  onboarded: boolean;
}

const DEFAULT_PROFILE: Profile = {
  name: '',
  role: 'Clinical Data Manager',
  avatar: 'avatar-01',
  smoothLocomotion: false,
  snapAngleDeg: 30,
  onboarded: false,
};

export function loadProfile(): Profile {
  const stored = loadItem<Partial<Profile>>('profile', {});
  const profile: Profile = { ...DEFAULT_PROFILE, ...stored };
  if (!RoleSchema.safeParse(profile.role).success) profile.role = DEFAULT_PROFILE.role;
  if (!BUILT_IN_AVATARS.some((a) => a.id === profile.avatar))
    profile.avatar = DEFAULT_PROFILE.avatar;
  profile.name = String(profile.name ?? '').slice(0, 24);
  if (!isValidName(profile.name)) profile.onboarded = false;
  return profile;
}

export function saveProfile(profile: Profile): void {
  saveItem('profile', profile);
}

export function isValidName(name: string): boolean {
  const trimmed = name.trim();
  return trimmed.length >= 2 && trimmed.length <= 24;
}
